import { FC, ReactElement, useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { OnboardingCipLoadingState, OnboardingCipState, loginState } from 'global-stores';
import { useNetwork } from 'hooks';
import { API_URL } from 'constant';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { Loader } from '@storybook';

import { SessionDetailsHeading } from '../session-details-heading';
import { SessionDetailsSkeletonLoader } from '../../session-details-loader';
import { useOnboardingCip } from '../../hooks/use-onboarding-cip';

import './tiered-onboarding.scss';

type Row = { label: string; value: any };
/** Operator override actions. `auto` = no manual override (system verdict stands, the default). */
type Decision = 'auto' | 'approved' | 'in-review' | 'rejected';
const ACTION_ORDER: Decision[] = ['auto', 'approved', 'in-review', 'rejected'];
const ACTION_LABEL: Record<Decision, string> = {
	auto: 'Auto',
	approved: 'Approve',
	'in-review': 'In review',
	rejected: 'Reject',
};
const ACTION_ICON: Record<Decision, string> = {
	auto: 'ri-flashlight-line',
	approved: 'ri-check-line',
	'in-review': 'ri-time-line',
	rejected: 'ri-close-line',
};
type Override = {
	decision?: Decision;
	previousStatus?: string | null;
	reason?: string;
	by?: string | null;
	byName?: string | null;
	at?: number;
};
type Review = { nodeId: string; overrides?: Override[] };
/**
 * `layout` controls the body grid:
 *  - 'pairs'    (default) — rows split down the middle into two label/value columns.
 *  - 'docTable' — a filename → status table (one row per document), for Document Review.
 */
type CardModel = {
	label: string;
	icon: ReactElement;
	rows: Row[];
	review?: Review;
	layout?: 'pairs' | 'docTable';
};

/** One session in the applicant's pipeline chain (root → children), as emitted by onboarding-cip. */
type ChainEntry = {
	sessionId?: string;
	isRoot?: boolean;
	pipelineId?: string | null;
	pipeline?: { name?: string | null; slug?: string | null };
	tier?: any;
	status?: string | null;
	currentStep?: string | null;
	kyc?: any;
	watchlist?: any;
	kyb?: any;
	questionnaire?: any[];
	documentReview?: any[];
	documentReviewNodeId?: string | null;
	documentReviewOverrides?: Override[];
};

/** Format a CIP value for display: booleans → Yes/No, ISO dates → locale, arrays → joined. */
const disp = (v: any): string => {
	if (v == null || v === '') return '--';
	if (typeof v === 'boolean') return v ? 'Yes' : 'No';
	if (Array.isArray(v)) return v.length ? v.map(disp).join(', ') : '--';
	if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
		const d = new Date(v);
		return isNaN(d.getTime()) ? v : d.toLocaleString();
	}
	if (typeof v === 'object') {
		const parts = Object.values(v).map(disp).filter(x => x !== '--');
		return parts.length ? parts.join(', ') : '--';
	}
	return String(v);
};

const addr = (a: any): string =>
	a
		? [a.line1, a.line2, a.city, a.state, a.postalCode, a.country].filter(Boolean).join(', ') || '--'
		: '--';

type ChipVariant = 'approved' | 'rejected' | 'review' | 'started' | 'neutral';

/** Map a free-text status onto the shared chip language (same hues as the header legend). */
const statusVariant = (raw: any): ChipVariant | null => {
	if (raw == null) return null;
	const s = String(raw).trim().toLowerCase();
	if (!s) return null;
	if (/(approv|complet|verified|success|passed|clear|match)/.test(s)) return 'approved';
	if (/(reject|declin|fail|denied|block)/.test(s)) return 'rejected';
	if (/(review|pending|manual|hold|await)/.test(s)) return 'review';
	if (/(start|progress|process|initiat|active|ongoing)/.test(s)) return 'started';
	if (/(not.?start|na|none|unassigned)/.test(s)) return 'neutral';
	return null;
};

/** A tonal status pill. Returns plain text when the value isn't a recognised status. */
const StatusChip: FC<{ value: any }> = ({ value }) => {
	const variant = statusVariant(value);
	if (!variant) return <>{disp(value)}</>;
	return <span className={`tcip-chip tcip-chip--${variant}`}>{disp(value)}</span>;
};

const icon = (name: string) => <i className={`${name} form-icon-profile-container__formIcon`} />;

const lastOverride = (overrides?: Override[]): Override | null =>
	Array.isArray(overrides) && overrides.length
		? overrides[overrides.length - 1] ?? null
		: null;

/**
 * One CIP stage card. When `review` + `canReview` are present it exposes a manual-override footer:
 * Approve/Reject reveal a MANDATORY reason box, then confirm. Any prior override shows as an audit line.
 */
const Card: FC<{
	card: CardModel;
	canReview: boolean;
	onDecision: (nodeId: string, decision: Decision, reason: string) => Promise<void>;
}> = ({ card, canReview, onDecision }) => {
	const [mode, setMode] = useState<Decision | null>(null);
	const [reason, setReason] = useState('');
	const [busy, setBusy] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);

	const prior = lastOverride(card.review?.overrides);
	// The override currently in effect (defaults to 'auto' = no manual override).
	const currentDecision: Decision =
		prior?.decision && ACTION_ORDER.includes(prior.decision)
			? prior.decision
			: 'auto';
	// A reason is mandatory for every action except reverting to 'auto'.
	const reasonRequired = !!mode && mode !== 'auto';

	// Header override dropdown: pick an action → collect the reason at the card bottom → confirm.
	// Picking the action already in effect just closes the menu (no-op).
	const choose = useCallback(
		(decision: Decision) => {
			setMenuOpen(false);
			if (decision === currentDecision) {
				setMode(null);
				setReason('');
				return;
			}
			setMode(decision);
			setReason('');
		},
		[currentDecision]
	);

	const submit = useCallback(async () => {
		if (!card.review?.nodeId || !mode) return;
		if (mode !== 'auto' && !reason.trim()) return;
		setBusy(true);
		try {
			await onDecision(card.review.nodeId, mode, reason.trim());
			setMode(null);
			setReason('');
		} finally {
			setBusy(false);
		}
	}, [card.review?.nodeId, mode, reason, onDecision]);

	const showReview = canReview && !!card.review?.nodeId;

	// Surface a card-level status chip in the header. For the document-review table the overall
	// status is derived from the rows (all acknowledged ⇒ Completed, else Pending); other cards
	// take it from their Status row.
	const statusRow = card.rows.find(r => /status/i.test(r.label));
	let headerStatus: ReactElement | undefined;
	if (card.layout === 'docTable' && card.rows.length) {
		const allAck = card.rows.every(
			r => String(r.value).toLowerCase() === 'acknowledged'
		);
		headerStatus = <StatusChip value={allAck ? 'Completed' : 'Pending'} />;
	} else if (statusRow && statusVariant(statusRow.value)) {
		headerStatus = <StatusChip value={statusRow.value} />;
	}

	const renderRow = (row: Row) => {
		const value = disp(row.value);
		return (
			<div className="tcip__cell" key={row.label}>
				<span className="tcip__key">{row.label}</span>
				<span className={`tcip__val${value === '--' ? ' tcip__val--empty' : ''}`}>
					{value}
				</span>
			</div>
		);
	};

	// When the status is already shown as the header chip, drop the redundant Status row from
	// the body. Split the remaining (logically ordered) rows down the middle into two columns.
	const bodyRows = headerStatus
		? card.rows.filter(r => r !== statusRow)
		: card.rows;
	const mid = Math.ceil(bodyRows.length / 2);
	const columns: Array<{ key: string; rows: Row[] }> = [
		{ key: 'left', rows: bodyRows.slice(0, mid) },
		{ key: 'right', rows: bodyRows.slice(mid) },
	];

	return (
		<div className="kycDetails-contain">
			<SessionDetailsHeading label={card.label} icon={card.icon}>
				<div className="tcip__header-right">
					{showReview && (
						<div className="tcip__ovr">
							<button
								type="button"
								className="tcip__ovr-trigger"
								disabled={busy}
								onClick={() => setMenuOpen(o => !o)}
							>
								<span>{ACTION_LABEL[mode ?? currentDecision]}</span>
								<i className="ri-arrow-down-s-line" />
							</button>
							{menuOpen && (
								<>
									<div
										className="tcip__ovr-backdrop"
										onClick={() => setMenuOpen(false)}
									/>
									<div className="tcip__ovr-menu" role="menu">
										{ACTION_ORDER.map(action => (
											<button
												key={action}
												type="button"
												className={`tcip__ovr-opt tcip__ovr-opt--${action}${
													action === currentDecision ? ' tcip__ovr-opt--active' : ''
												}`}
												onClick={() => choose(action)}
											>
												<i className={ACTION_ICON[action]} />
												<span className="tcip__ovr-opt__label">{ACTION_LABEL[action]}</span>
												{action === currentDecision && (
													<i className="ri-check-line tcip__ovr-opt__tick" />
												)}
											</button>
										))}
									</div>
								</>
								)}
						</div>
					)}
					{headerStatus}
				</div>
			</SessionDetailsHeading>
			{card.layout === 'docTable' ? (
				<div className="tcip__doctable">
					{card.rows.map(row => (
						<div className="tcip__doctable-row" key={row.label}>
							<span className="tcip__doctable-name">{row.label}</span>
							<span className="tcip__doctable-status">{disp(row.value)}</span>
						</div>
					))}
				</div>
			) : (
				<div className="tcip__grid">
					{columns.map(col => (
						<div className="tcip__col" key={col.key}>
							{col.rows.map(renderRow)}
						</div>
					))}
				</div>
			)}

			{/* Reason capture — opened by choosing an action from the header dropdown. */}
			{showReview && mode && (
				<div className="tcip__review">
					<textarea
						className="tcip__reason"
						placeholder={`Reason for ${ACTION_LABEL[
							mode
						].toLowerCase()}${reasonRequired ? ' (required)' : ' (optional)'}`}
						value={reason}
						onChange={e => setReason(e.target.value)}
						rows={2}
						autoFocus
					/>
					<div className="tcip__actions">
						<button
							className={`tcip__btn tcip__btn--${
								mode === 'approved'
									? 'approve'
									: mode === 'rejected'
									? 'reject'
									: 'ghost'
							}`}
							disabled={busy || (reasonRequired && !reason.trim())}
							onClick={submit}
						>
							{busy ? (
								<Loader type="loader" dimension={12} className="loader-white" />
							) : (
								`Confirm ${ACTION_LABEL[mode]}`
							)}
						</button>
						<button
							className="tcip__btn tcip__btn--ghost"
							disabled={busy}
							onClick={() => {
								setMode(null);
								setReason('');
							}}
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			{/* Existing manual-override record — the who / why / when audit trail. */}
			{prior && (
				<div className="tcip__ovr-record">
					<div className="tcip__ovr-record__head">
						<i className="ri-shield-user-line" />
						Manual override
						<StatusChip value={ACTION_LABEL[prior.decision ?? 'auto']} />
					</div>
					<div className="tcip__ovr-record__grid">
						<span className="tcip__key">By</span>
						<span className="tcip__val">
							{prior.byName || prior.by || 'Operator'}
						</span>
						<span className="tcip__key">When</span>
						<span className="tcip__val">
							{prior.at ? new Date(prior.at).toLocaleString() : '--'}
						</span>
						<span className="tcip__key">Reason</span>
						<span className="tcip__val">{prior.reason || '--'}</span>
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * A tier/pipeline header card — no detail body. Three columns: icon · (name over
 * `slug • pipelineId`) · tier status.
 */
const TierHeaderCard: FC<{
	name: string;
	slug?: string | null;
	pipelineId?: string | null;
	status?: string | null;
}> = ({ name, slug, pipelineId, status }) => (
	<div className="kycDetails-contain tcip__tier">
		<div className="tcip__tier-head">
			<span className="form-icon-container tcip__tier-icon">
				<span className="form-icon-profile-container">
					<i className="ri-stack-fill form-icon-profile-container__formIcon" />
				</span>
			</span>
			<div className="tcip__tier-meta">
				<span className="tcip__tier-name">{name}</span>
				{(slug || pipelineId) && (
					<span className="tcip__tier-sub">
						{slug && <span className="tcip__tier-slug">{slug}</span>}
						{slug && pipelineId && <span className="tcip__tier-dot">•</span>}
						{pipelineId && <span className="tcip__tier-id">{pipelineId}</span>}
					</span>
				)}
			</div>
			{statusVariant(status) && <StatusChip value={status} />}
		</div>
	</div>
);

/** The stage cards (KYC, AML, KYB, Questionnaire, Document Review) for one chain entry. */
const stageCards = (entry: ChainEntry): CardModel[] => {
	const out: CardModel[] = [];

	if (entry.kyc) {
		const k = entry.kyc;
		const vf = k.verified ?? {};
		out.push({
			label: 'KYC',
			icon: icon('ri-shield-check-fill'),
			rows: [
				{ label: 'Provider', value: k.provider },
				{ label: 'Status', value: k.status },
				{ label: 'Match type', value: k.matchType },
				{ label: 'Face match score', value: k.faceMatchScore },
				{ label: 'SSN required', value: k.ssnRequired },
				{ label: 'SSN verified', value: k.ssnVerified },
				{
					label: 'Verified fields',
					value: Object.entries(vf)
						.filter(([, val]) => val === true)
						.map(([key]) => key),
				},
				{ label: 'Check ID', value: k.checkId },
			],
			review: k.reviewNodeId ? { nodeId: k.reviewNodeId, overrides: k.overrides } : undefined,
		});
	}

	if (entry.kyb) {
		const b = entry.kyb;
		out.push({
			label: 'KYB',
			icon: icon('ri-building-4-fill'),
			rows: [
				{ label: 'Provider', value: b.provider },
				{ label: 'Status', value: b.status },
				{ label: 'Check ID', value: b.checkId },
			],
			review: b.reviewNodeId ? { nodeId: b.reviewNodeId, overrides: b.overrides } : undefined,
		});
	}

	if (entry.watchlist) {
		const w = entry.watchlist;
		out.push({
			label: 'Watchlist / AML',
			icon: icon('ri-scales-3-fill'),
			rows: [
				{ label: 'Status', value: w.status },
				{ label: 'Providers', value: w.providers },
				{ label: 'Lists screened', value: w.lists },
				{ label: 'Match count', value: w.matchCount },
			],
			review: w.reviewNodeId ? { nodeId: w.reviewNodeId, overrides: w.overrides } : undefined,
		});
	}

	(entry.questionnaire ?? []).forEach((q: any, i: number) => {
		const answers = q.answers ?? {};
		const rows: Row[] = Object.keys(answers).length
			? Object.entries(answers).map(([label, value]) => ({ label, value }))
			: [{ label: 'Answers', value: '--' }];
		out.push({
			label: `Questionnaire${(entry.questionnaire?.length ?? 0) > 1 ? ` ${i + 1}` : ''}${
				q.label ? ` — ${q.label}` : ''
			}`,
			icon: icon('ri-questionnaire-fill'),
			rows,
			review: q.reviewNodeId ? { nodeId: q.reviewNodeId, overrides: q.overrides } : undefined,
		});
	});

	if ((entry.documentReview ?? []).length) {
		out.push({
			label: 'Document Review',
			icon: icon('ri-file-list-3-fill'),
			layout: 'docTable',
			rows: (entry.documentReview ?? []).map((d: any) => ({
				label: String(d.docId),
				value: d.acknowledged ? 'Acknowledged' : 'Not acknowledged',
			})),
			review: entry.documentReviewNodeId
				? { nodeId: entry.documentReviewNodeId, overrides: entry.documentReviewOverrides }
				: undefined,
		});
	}

	return out;
};

/**
 * Tiered onboarding (Tier-0/Tier-1/…) detail block. Reads the single `OnboardingCipState` fetch
 * (`GET /onboarding-cip/:id`) and renders the FULL pipeline chain: applicant identity, then one
 * section per session/pipeline the applicant touched (tier0 → tier1 → tier2 …) with that session's
 * own stage cards + manual override (approve/reject with a mandatory reason), then consent/audit.
 * Falls back to a single-entry view when the payload has no `chain`.
 */
export const TieredOnboarding: FC<{ section?: 'identity' | 'pipelines' }> = ({
	section,
}) => {
	const showIdentity = !section || section === 'identity';
	const showPipelines = !section || section === 'pipelines';
	const cip = useRecoilValue(OnboardingCipState);
	const loading = useRecoilValue(OnboardingCipLoadingState);
	const { name } = useRecoilValue(loginState) ?? ({} as any);
	const { post } = useNetwork();
	const { fetchCip } = useOnboardingCip();
	const { checkUserWritePermission } = useUserRoles();

	const canReview = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sessions),
		[checkUserWritePermission]
	);

	const anchorId = cip?.sessionId ?? cip?.onboardingSessionId;

	const onDecision = useCallback(
		async (nodeId: string, decision: Decision, reason: string) => {
			const resp = await post(API_URL.ONBOARDING_TIER1, {
				step: 'override',
				nodeId,
				decision,
				reason,
				operatorName: name ?? undefined,
			});
			if (resp) await fetchCip(anchorId);
		},
		[post, fetchCip, anchorId, name]
	);

	// Prefer the server chain; else synthesize a single entry from the flat payload (back-compat).
	const chain: ChainEntry[] = useMemo(() => {
		if (Array.isArray(cip?.chain) && cip.chain.length) return cip.chain;
		if (!anchorId) return [];
		return [
			{
				sessionId: cip?.sessionId,
				isRoot: true,
				pipeline: { slug: null, name: null },
				tier: cip?.tier?.current,
				status: cip?.status,
				kyc: cip?.kyc,
				watchlist: cip?.watchlist,
				kyb: cip?.kyb,
				questionnaire: cip?.questionnaire,
				documentReview: cip?.documentReview,
				documentReviewNodeId: cip?.documentReviewNodeId,
				documentReviewOverrides: cip?.documentReviewOverrides,
			},
		];
	}, [cip, anchorId]);

	const applicantCard: CardModel | null = useMemo(() => {
		const a = cip?.applicant;
		if (!a) return null;
		return {
			label: 'Applicant Identity',
			icon: icon('ri-user-3-fill'),
			rows: [
				{ label: 'Full name', value: a.fullName },
				{ label: 'Date of birth', value: a.dateOfBirth },
				{ label: 'Nationality', value: a.nationality },
				{ label: 'Email', value: a.email },
				{ label: 'Phone', value: a.phone ? `${a.countryCode ?? ''} ${a.phone}`.trim() : '--' },
				{ label: 'Address', value: addr(a.address) },
				{ label: 'ID type', value: a.idType },
				{ label: 'ID number', value: a.idNumber },
				{ label: 'ID issuing state', value: a.idIssuingState },
				{ label: 'ID expiry', value: a.idExpiry },
				{ label: 'SSN verified', value: a.ssnVerified },
			],
		};
	}, [cip]);

	/* Consent & Audit, rebuilt as its own memo.
	 *
	 * The JSX below renders `consentCard`, but its declaration had been lost: what
	 * sat here was ~90 orphaned lines pushing KYC, KYB, Watchlist/AML,
	 * Questionnaire, Document Review and Consent & Audit onto `out` — a variable
	 * that only exists inside stageCards(), not in this component. That could
	 * never compile, which is why the dashboard was serving a build-error overlay.
	 *
	 * Only this card is rebuilt. The other four are already produced by
	 * stageCards() from the same data, so restoring them here would render each
	 * twice. The Questionnaire card is NOT restored: it existed only in the dead
	 * copy, and reinstating it is a behaviour change rather than a repair.
	 */
	const consentCard: CardModel | null = useMemo(() => {
		if (!cip) return null;
		const c = cip.consent ?? {};
		const au = cip.audit ?? {};
		return {
			label: 'Consent & Audit',
			icon: icon('ri-shield-user-fill'),
			rows: [
				{ label: 'Consent accepted', value: c.accepted },
				{ label: 'Consent version', value: c.version },
				{ label: 'Consent scope', value: c.scope },
				{
					label: 'Consent timestamp',
					value: c.timestamp ? new Date(c.timestamp).toLocaleString() : '--',
				},
				{ label: 'IP address', value: au.ipAddress },
				{ label: 'Observed IP', value: au.observedIp },
			],
		};
	}, [cip]);

	if (loading && !anchorId) {
		// Show the skeleton once (on the pipelines side) to avoid a duplicate loader.
		if (!showPipelines) return null;
		return (
			<div className="kycDetails-contain">
				<SessionDetailsHeading label="Tiered Onboarding" icon={icon('ri-stack-fill')} />
				<SessionDetailsSkeletonLoader />
			</div>
		);
	}

	if (!anchorId) return null;

	const tierName = (e: ChainEntry, i: number) => {
		const base =
			e.pipeline?.name ||
			e.pipeline?.slug ||
			(e.isRoot ? 'Root' : `Pipeline ${i + 1}`);
		return `${base}${e.isRoot ? ' — Root' : ''}`;
	};

	// Consent & Audit is anchored under the Tier-1 pipeline (first non-root entry), rendered as that
	// section's first card. Falls back to the chain tail when there is no Tier-1 entry.
	const tier1Index = chain.findIndex(e => !e.isRoot);

	return (
		<div className="tcip">
			{showIdentity && applicantCard && (
				<Card card={applicantCard} canReview={false} onDecision={onDecision} />
			)}

			{showPipelines && chain.map((entry, i) => {
				const cards = stageCards(entry);
				return (
					<div className="tcip__chain" key={entry.sessionId ?? i}>
						<TierHeaderCard
							name={tierName(entry, i)}
							slug={entry.pipeline?.slug}
							pipelineId={entry.pipelineId}
							status={entry.status}
						/>
						{consentCard && i === tier1Index && (
							<Card card={consentCard} canReview={false} onDecision={onDecision} />
						)}
						{cards.map(card => (
							<Card
								key={`${entry.sessionId}-${card.label}`}
								card={card}
								canReview={canReview}
								onDecision={onDecision}
							/>
						))}
					</div>
				);
			})}

			{showPipelines && consentCard && tier1Index === -1 && (
				<Card card={consentCard} canReview={false} onDecision={onDecision} />
			)}
		</div>
	);
};
